"use client"

import { useEffect, useEffectEvent, useRef, useState } from "react"
import { usePathname } from "next/navigation"
import { ArrowRight, Check, X } from "lucide-react"

import { MarketingButton, marketingIconActionClass } from "@/components/marketing/marketing-action"
import { marketingFieldClass } from "@/components/marketing/marketing-field"
import { marketingSurfaceClass } from "@/components/marketing/marketing-surface"
import { SectionEyebrow } from "@/components/marketing/section-eyebrow"
import { MarketingAlert } from "@/components/marketing/marketing-alert"
import { NewsletterUnreachableLine } from "@/components/marketing/newsletter-signup"
import { useMarketingAuth } from "@/components/marketing/use-marketing-auth"
import { AnalyticsEvent, stitchLeadEmail, track } from "@/lib/analytics"
import { EMAIL_RE } from "@/lib/utils"
import { requestNewsletterSignup } from "@/lib/newsletter/signup-request"
import { HoneypotField } from "@/components/marketing/honeypot-field"
import {
  INTRO_CAPTURED_COOKIE,
  INTRO_CAPTURED_VALUE,
  SHARED_EMAIL_COOKIES,
  UNLOCK_COOKIE_MAX_AGE,
} from "@/lib/articles/unlock-cookie"

// Owner rulings (research interview): the popup runs on an ALLOW-LIST — the
// landing page, the article library, and the non-selling browse pages — never
// on an offer, coaching, VSL, funnel, or email-gate page, where it costs a
// sale or stops the visitor reading. A new page is popup-free by default.
//
// Trigger: a 10-second timer. Vendor curves peak in the 6-15s bucket
// (Wisepops 6.45% at 11-15s vs 4.16% immediate; Omnisend 6-10s at 2.4% vs
// 1.9% on-load), and a real delay counts as post-engagement under Google's
// interstitial rules. Cadence: a dismissal buys ~7 days of quiet via a
// cool-down cookie (popup emails unsubscribe at the worst rate of any capture
// source, so pressure eases); an email ends it forever; closing also still
// silences the rest of the current visit.
//
// "Captured" honors EVERY shared email cookie (`SHARED_EMAIL_COOKIES`: the
// newsletter signup's cookie plus the retired names nothing sets any more),
// not just its own, because re-asking a subscriber for the address he already
// handed over reads as broken. Logged-in users never see it.
//
// The popup belongs to the page it opened on, and only while he stays there.
// It lives in the shared marketing layout, so it survives a client-side
// navigation: an iPhone visitor tapped "Find your program", the
// reveal fired 83 ms later while that navigation was still pending (the URL
// only changes when the next page has arrived, and his stalled connection took
// 70 s), and when /apply landed the modal was still on top of the application
// form, its "Sending..." signup stuck behind the same stalled connection.
// So: a tap on a link that leaves the page cancels the pending reveal (the next
// page arms its own), any navigation closes an open popup without counting a
// dismissal, and a signup answer that lands after it closed records the
// signup but never reopens it. The signup request itself is bounded
// (lib/newsletter/signup-request.ts). web/e2e/intro-popup-never-blocks-apply.spec.ts
// covers all three.
const DISMISSED_COOKIE = "ca_intro_dismissed"
const DISMISSED_DAYS = 7
const SESSION_SEEN_KEY = "ca_intro_seen"
const SHOW_DELAY_MS = 10_000
// The HTML `pattern` attribute takes a string; the runtime check is the shared EMAIL_RE.
const EMAIL_PATTERN = "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$"

type PopupState = "checking" | "hidden" | "ready" | "loading" | "ok" | "error"
type PopupVariant = "modal"

/** The banner/modal variants' email input - identical attributes except the
 * size className. Neither variant takes focus on open. */
function EmailField({
  email,
  setEmail,
  error,
  className,
}: {
  email: string
  setEmail: (value: string) => void
  error: string
  className: string
}) {
  return (
    <input
      type="email"
      name="email"
      required
      maxLength={254}
      pattern={EMAIL_PATTERN}
      title="Enter a valid email address."
      inputMode="email"
      aria-label="Email address"
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? "intro-course-popup-error" : undefined}
      value={email}
      onChange={(event) => setEmail(event.target.value)}
      placeholder="Email address"
      autoComplete="email"
      className={marketingFieldClass({ className })}
    />
  )
}

function hasCapturedEmail() {
  if (typeof document === "undefined") return true
  const names = document.cookie
    .split(";")
    .map((part) => part.trim().split("=")[0])
  return SHARED_EMAIL_COOKIES.some((cookie) => names.includes(cookie))
}

// Secure whenever the page itself is on https, so the cookie never rides a
// plain-http request; local dev on http://localhost still gets it.
function secureAttribute() {
  return window.location.protocol === "https:" ? "; secure" : ""
}

function markCaptured() {
  if (typeof document === "undefined") return
  document.cookie = `${INTRO_CAPTURED_COOKIE}=${INTRO_CAPTURED_VALUE}; path=/; max-age=${UNLOCK_COOKIE_MAX_AGE}; samesite=lax${secureAttribute()}`
}

function recentlyDismissed() {
  if (typeof document === "undefined") return true
  return document.cookie
    .split(";")
    .map((part) => part.trim())
    .some((part) => part.startsWith(`${DISMISSED_COOKIE}=`))
}

function markDismissed() {
  if (typeof document === "undefined") return
  const maxAge = DISMISSED_DAYS * 24 * 60 * 60
  document.cookie = `${DISMISSED_COOKIE}=1; path=/; max-age=${maxAge}; samesite=lax${secureAttribute()}`
}

function seenThisVisit() {
  try {
    return window.sessionStorage.getItem(SESSION_SEEN_KEY) === "1"
  } catch {
    return false
  }
}

function markSeenThisVisit() {
  try {
    window.sessionStorage.setItem(SESSION_SEEN_KEY, "1")
  } catch {
    // sessionStorage unavailable (private mode etc.) — fine, it just shows again.
  }
}

/** The article library: the pages whose mobile render Google's interstitial
 * penalty judges, because they are what search sends phones to. The reading
 * meter's subscribe wall sits under /articles/ but is an offer page (unlimited
 * reading for a monthly price), so it is not library and stays popup-free. */
function isArticleSurface(pathname: string) {
  if (pathname === "/articles/subscribe") return false
  return (
    pathname === "/articles" ||
    pathname.startsWith("/articles/") ||
    pathname.startsWith("/categories/")
  )
}

/** True when this click follows a link off the current page in this tab: the
 * navigation it starts only changes the URL once the next page has arrived. A
 * link that opens elsewhere (a new tab, a download, a modified click) or only
 * moves within this page (a hash, a query) leaves him here. */
function leavesThisPage(event: MouseEvent): boolean {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false
  const link = event.target instanceof Element ? event.target.closest("a[href]") : null
  if (!(link instanceof HTMLAnchorElement)) return false
  if ((link.target && link.target !== "_self") || link.hasAttribute("download")) return false
  const to = new URL(link.href, window.location.href)
  return to.origin !== window.location.origin || to.pathname !== window.location.pathname
}

export function IntroCoursePopup() {
  const { authed } = useMarketingAuth()
  const pathname = usePathname()
  const [state, setState] = useState<PopupState>("checking")
  const [variant, setVariant] = useState<PopupVariant>("modal")
  const [email, setEmail] = useState("")
  const [error, setError] = useState("")
  // POST /api/newsletter answered `subscribed: false`: our email cannot reach
  // this address, so the form says so instead of "check your email".
  const [unreachable, setUnreachable] = useState(false)
  const dialogRef = useRef<HTMLDivElement | null>(null)

  // Any navigation closes an open popup: it belonged to the page he left. Not a
  // dismissal (no cool-down cookie, no dismissed event); this visit already
  // counts as having seen it, so it does not come back on the next page.
  // Adjusted during render, not in an effect, so the modal never paints on the
  // next page for even one frame.
  const [shownOn, setShownOn] = useState(pathname)
  if (shownOn !== pathname) {
    setShownOn(pathname)
    if (state !== "checking" && state !== "hidden") setState("hidden")
  }

  // The global :focus-visible rule outlines whatever holds focus in crimson,
  // so a programmatically focused card wears a crimson border the approved
  // design does not have. A wrapper nobody chose to interact with does not
  // need that ring; every real control inside it keeps one.
  //
  // Focus moves INTO the dialog when it opens, but onto the dialog itself
  // rather than the email field. autoFocus on the input was what put a
  // crimson focus ring around the field the instant the popup appeared,
  // which is not the card he approved and reads as a validation error.
  // A container with tabIndex -1 keeps Escape and Tab working without
  // painting that ring.

  // The allow-list. Landing, the article library, and the non-selling browse
  // pages (/testimonials, /complete-dating-system, /compare). The retired
  // /playbooks index and /playbooks/[slug] detail paths redirect to the
  // flagship, so this check carries no entry for either one. The ten Playbooks
  // are pages inside the course now. Checkout, /apply, /book,
  // /what-happens-next, every offer and
  // coaching page, the VSLs, the email-gate pages, /unsubscribe and /ops are
  // popup-free BY OMISSION — the class of "carved out one page too late"
  // cannot recur.
  const allowed =
    pathname === "/" ||
    isArticleSurface(pathname) ||
    pathname === "/testimonials" ||
    pathname === "/compare" ||
    pathname === "/complete-dating-system"

  // Decide on mount whether to reveal the popup for this visit. The cookie /
  // sessionStorage checks are browser-only, so they run after mount; if the
  // visitor qualifies, reveal after the delay.
  useEffect(() => {
    if (!allowed || authed || hasCapturedEmail() || recentlyDismissed() || seenThisVisit()) return
    const timer = window.setTimeout(() => {
      const revealVariant: PopupVariant = "modal"
      markSeenThisVisit()
      setVariant(revealVariant)
      setState("ready")
      track(AnalyticsEvent.IntroPopupShown, { pathname, variant: revealVariant })
    }, SHOW_DELAY_MS)
    // He is already leaving: a tap on a link off this page cancels the reveal
    // for the rest of this page's life, since the URL (and so this effect) only
    // moves once the next page arrives. Capture phase, so it runs before the
    // Link's own handler starts the navigation.
    const onClick = (event: MouseEvent) => {
      if (leavesThisPage(event)) window.clearTimeout(timer)
    }
    document.addEventListener("click", onClick, true)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener("click", onClick, true)
    }
  }, [authed, allowed, pathname])

  const open = state === "ready" || state === "loading" || state === "ok" || state === "error"

  // Lock background scroll while the MODAL is open. The banner never locks —
  // an article must stay readable and scrollable underneath it.
  useEffect(() => {
    if (!open || variant !== "modal") return
    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = previous
    }
  }, [open, variant])

  // Move focus into the dialog, onto the dialog itself.
  useEffect(() => {
    if (!open || variant !== "modal") return
    dialogRef.current?.focus()
  }, [open, variant])

  // Close on Escape. Closing sets the 7-day cool-down; only an email ends it.
  // An effect event, so Escape on the thank-you reads the current state and is
  // not counted as a dismissal the way a listener frozen at opening was.
  const onEscape = useEffectEvent(() => dismiss())
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onEscape()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open])

  function dismiss() {
    // A dismissal after a successful signup is just closing the thank-you —
    // the capture cookie already silences the popup forever, and counting it
    // as a "dismissed" impression would poison the view-to-signup numbers.
    if (state !== "ok") {
      markDismissed()
      track(AnalyticsEvent.IntroPopupDismissed, { pathname, variant })
    }
    setState("hidden")
  }

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (state === "loading") return
    const normalized = email.trim().toLowerCase()
    if (!normalized || !EMAIL_RE.test(normalized) || normalized.length > 254) {
      setError("Enter a valid email address.")
      setState("error")
      return
    }
    setState("loading")
    setError("")
    setUnreachable(false)

    // Every answer below moves the popup only if it is still waiting on this
    // request: one that lands after he closed it, or after a navigation closed
    // it, leaves it closed.
    const settle = (next: PopupState) => setState((current) => (current === "loading" ? next : current))
    const result = await requestNewsletterSignup(normalized, "intro-course-popup", e.currentTarget)
    if (result.kind === "error") {
      setError(result.message)
      settle("error")
      return
    }
    if (result.kind === "unreachable") {
      setError("unreachable")
      setUnreachable(true)
      settle("error")
      return
    }
    // Email captured — never show the popup again on this device, even when the
    // answer lands after he left the page. POST /api/newsletter is a real
    // newsletter subscribe: it writes the newsletter_subscribers row, sends the
    // welcome email, and enrolls the day-1/3/7 nurture legs. Nothing
    // redirects; they close and keep going.
    markCaptured()
    stitchLeadEmail(normalized)
    track(AnalyticsEvent.NewsletterSignup, { source: "intro-course-popup" })
    // The newsletter bubble is a second door into LEAD_CAPTURED (his
    // addition: the subscriber and the lead are the same person), so the
    // capture fires the pipeline's own event beside the newsletter one. The
    // `door` names which surface took the contact; the form sends "apply".
    // No meta_event_id rides here, so the Meta Lead pixel does not fire for
    // this door (it needs the server-issued dedup id only /apply hands back).
    track(AnalyticsEvent.LeadCaptured, { door: "newsletter_bubble", pathname, variant })
    settle("ok")
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/55"
        aria-hidden
        onClick={dismiss}
      />
      <div
        ref={dialogRef}
        tabIndex={-1}
        data-dialog-surface
        role="dialog"
        aria-labelledby="intro-course-popup-title"
        aria-modal="true"
        className={marketingSurfaceClass({
          className:
            "relative z-10 w-full max-w-[35rem] rounded-[18px] px-6 pb-[1.875rem] pt-10 text-center shadow-[0_24px_64px_rgba(0,0,0,0.28),0_4px_16px_rgba(0,0,0,0.12)] sm:rounded-[20px] sm:px-12 sm:pb-10 sm:pt-[3.25rem]",
          elevation: "overlay",
          emphasis: "quiet",
          tone: "high",
        })}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close newsletter signup"
          className={marketingIconActionClass({
            className:
              "absolute right-3.5 top-3.5 h-8 w-8 rounded-full border-transparent bg-[var(--color-paper-deep)] max-sm:after:absolute max-sm:after:-inset-2 max-sm:after:content-[''] sm:right-4 sm:top-4",
            size: "sm",
            variant: "secondary",
          })}
        >
          <X size={16} aria-hidden />
        </button>

        {state === "ok" ? (
          <div className="flex flex-col items-center gap-5 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-vermilion)]">
              <Check size={28} strokeWidth={2.5} className="text-white" aria-hidden />
            </span>
            <div className="flex flex-col gap-2">
              <p
                id="intro-course-popup-title"
                className="serious-display text-[1.6rem] leading-[1.12] text-[var(--color-ink)]"
              >
                You&apos;re all set.
              </p>
              <p className="text-[0.98rem] leading-[1.55] text-[var(--color-ink-muted)]">
                Check your email. A free article lands in your inbox every week. Close this whenever you&apos;re ready.
              </p>
            </div>
            <MarketingButton
              type="button"
              onClick={dismiss}
              size="lg"
              variant="accent"
              className="h-[3.25rem] w-full rounded-[14px]"
            >
              Got it
            </MarketingButton>
          </div>
        ) : (
          <form method="post" onSubmit={submit} noValidate className="relative flex flex-col">
            <HoneypotField />
            <SectionEyebrow size="sm" className="mb-3 sm:mb-3.5">
              Newsletter
            </SectionEyebrow>
            <p
              id="intro-course-popup-title"
              className="serious-display text-[1.5625rem] leading-[1.16] tracking-[-0.015em] text-[var(--color-ink)] sm:text-[1.9375rem]"
            >
              Ready to learn real game?
            </p>
            <p className="mx-auto mt-2.5 max-w-[42ch] text-[0.9375rem] leading-[1.55] text-[var(--color-ink-muted)] sm:mt-3 sm:text-[1rem]">
              Sign up for our newsletter and get exclusive dating advice, promotions, news and information about upcoming events delivered straight to your inbox
            </p>

            <div className="mt-6 flex flex-col gap-2.5 sm:mt-7 sm:gap-3">
              <EmailField
                email={email}
                setEmail={setEmail}
                error={error}
                className="h-[3.25rem] rounded-[14px] px-[1.125rem] text-[0.9375rem]"
              />
              <MarketingButton
                type="submit"
                disabled={state === "loading"}
                size="lg"
                variant="accent"
                className="h-[3.25rem] w-full rounded-[14px]"
              >
                {state === "loading" ? "Sending..." : "Sign up"}
                <ArrowRight size={15} aria-hidden />
              </MarketingButton>
              {error ? (
                <MarketingAlert id="intro-course-popup-error" className="py-2 px-3 text-[0.88rem]">
                  {unreachable ? <NewsletterUnreachableLine /> : error}
                </MarketingAlert>
              ) : null}
            </div>

            <p className="mt-3.5 text-[0.75rem] leading-[1.5] text-[var(--color-ink-soft)] sm:mt-4 sm:text-[0.8125rem]">
              Unsubscribe anytime.
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
